import React, { Fragment, memo, useEffect } from 'react';
import Header from './presenters/Header';
import InitialDataFetcher from './controllers/InitialDataFetcher';
import { ValidateSelectedCompany } from './controllers/SelectCompany';
import AcceptanceDetailsHeader from './presenters/AcceptanceDetailsHeader';
import { useSearchParamsSelector } from '../../shared/packages/searchParamsManager';
import {
  searchParamKeys,
  searchParamSelectors,
  viewMap,
} from '../../shared/configs/searchParams';
import ToggleView from './controllers/ToggleView';
import Collapse from '../../shared/ui/Collapse';
import AcceptanceIdValidator from './controllers/AcceptanceIdValidator';
import DeskTags from './controllers/DeskTags';
import CalendarView from './presenters/CalendarView';
import ViewGuard from './controllers/ViewGuard';
import {
  CurrentAcceptanceProvider,
  useCurrentAcceptance,
} from '../../services/queries/acceptanceData';
import ChangeAcceptanceDateModal from './controllers/ChangeAcceptanceDate';
import { RoomsTable } from '../../controllers/RoomsTable';
import SelectOwnerForRegistrationModal from './controllers/SelectOwnerForRegistrationModal';
import SetupIntervalForRegistrationModal from './controllers/SetupIntervalForRegistrationModal';
import { useRoomListViewData } from '../../services/queries/acceptanceViewFlats';
import {
  ChangeStageModal,
  ChangeStepModal,
  ChangeAcceptanceStatusModal,
  MassChangeLifeCycle,
} from '../../controllers/ChangeLifeCycle';
import RoomInfoDrawer from '../../controllers/RoomInfoDrawer';
import AddEditAttachmentsModal from '../../controllers/AttachmentsFormModal';
import { RegistrationProvider } from '../../services/acceptanceRegistration/provider';
import { CalendarProvider } from '../../services/queries/calendar';
import { DeferredResetStateOnAcceptanceChangeWrapper } from './services/deferredResetStateOnAcceptanceChange';
import PinnedSideBar from './presenters/PinnedSideBar';
import { AddTenantModal } from '../../controllers/AddTenant';
import { useAllowHousingStock } from '../../shared/hooks/allowHousingStock';
import {
  RoomsSelectionProvider,
  useRoomsSelectionActions,
  useRoomsSelectionConfig,
} from './services/roomsSelection';
import { useUserAcceptanceRole } from '../../shared/hooks/userAcceptanceRole';
import { acceptanceViewFlatsSelectors } from '../../api/acceptanceViewFlats';
import MassActions from './presenters/MassActions';
import styles from './index.module.scss';

const { useActions: useMassChangeLifeCycleActions } = MassChangeLifeCycle;

const MemoizedView = memo(View);

const DetailsPage = ({ className }) => {
  const allowHousingStock = useAllowHousingStock();

  const role = useUserAcceptanceRole();

  const MassActionsWrapper = role.isDirectorOrManager
    ? MassChangeLifeCycleSetup
    : Fragment;

  return (
    <InitialDataFetcher className={className}>
      <div className={styles.root}>
        <Header />

        <ValidateSelectedCompany>
          <div className={styles.main}>
            <PinnedSideBar className={styles.sidebar} />

            <div className={styles.content}>
              <AcceptanceIdValidator>
                <CurrentAcceptanceProvider>
                  <div className={styles.acceptanceDetailsWrapper}>
                    <CurrentCalendarProvider>
                      <RegistrationProvider>
                        <RoomsSelectionProvider>
                          <MassActionsWrapper>
                            <Collapse expandIconPosition="right">
                              <AcceptanceDetailsHeader
                                // filterSlot={<FilterSlot />}
                                toggleViewSlot={<ToggleView />}
                                desksSlot={<DeskTags />}
                                massActionsSlot={
                                  role.isDirectorOrManager && <MassActions />
                                }
                              />
                            </Collapse>
                            <ViewGuard>
                              <MemoizedView />
                              {role.isDirectorOrManager && (
                                <>
                                  <SelectOwnerForRegistrationModal />
                                  <SetupIntervalForRegistrationModal />
                                </>
                              )}
                              {allowHousingStock && <AddTenantModal />}
                            </ViewGuard>
                          </MassActionsWrapper>
                        </RoomsSelectionProvider>
                      </RegistrationProvider>
                    </CurrentCalendarProvider>
                  </div>
                  <ChangeAcceptanceDateModal />
                </CurrentAcceptanceProvider>
              </AcceptanceIdValidator>
            </div>
          </div>
        </ValidateSelectedCompany>
      </div>
    </InitialDataFetcher>
  );
};

export default DetailsPage;

function View() {
  const view = useSearchParamsSelector(
    searchParamSelectors[searchParamKeys.view]
  );

  return (
    <DeferredResetStateOnAcceptanceChangeWrapper>
      {view === viewMap.calendar && <CalendarView />}
      {view === viewMap.list && <RoomList />}
    </DeferredResetStateOnAcceptanceChangeWrapper>
  );
}

function CurrentCalendarProvider({ children }) {
  const acceptance = useCurrentAcceptance();

  return (
    <CalendarProvider
      fromDate={acceptance.fromDate}
      toDate={acceptance.toDate}
      desks={acceptance.desks}
    >
      {children}
    </CalendarProvider>
  );
}

function RoomList() {
  const role = useUserAcceptanceRole();

  const canChangeLifeCicle = role.isDirectorOrManager;

  return (
    <>
      <RoomsTableSetup canChangeLifeCycle={canChangeLifeCicle} />

      {canChangeLifeCicle && (
        <>
          <ChangeStageModal />
          <ChangeStepModal />
          <ChangeAcceptanceStatusModal />
        </>
      )}

      <RoomInfoDrawer>
        <AddEditAttachmentsModal />
      </RoomInfoDrawer>
    </>
  );
}

const emptyArr = [];
function RoomsTableSetup({ canChangeLifeCycle }) {
  const { data, isFetching, refetch } = useRoomListViewData();

  const acceptance = useCurrentAcceptance();
  const rowSelectionConfig = useRoomsSelectionConfig();

  const massChangeLifeCycleActions = useMassChangeLifeCycleActions();
  const { setRoomsMap } = massChangeLifeCycleActions ?? {};

  const rooms = Array.isArray(data) ? data : emptyArr;

  useEffect(() => {
    if (typeof setRoomsMap === 'function') {
      setRoomsMap(acceptanceViewFlatsSelectors.toMap(rooms));
    }
  }, [rooms, setRoomsMap]);

  return (
    <RoomsTable
      withCheckbox={canChangeLifeCycle}
      data={data}
      isFetching={isFetching}
      refetch={refetch}
      acceptance={acceptance}
      rowSelectionConfig={rowSelectionConfig}
      accesses={{
        [RoomsTable.accessesKeys.canChangeLifeCycle]: canChangeLifeCycle,
      }}
    />
  );
}

function MassChangeLifeCycleSetup({ children }) {
  const { onReset } = useRoomsSelectionActions();

  return (
    <MassChangeLifeCycle.Provider onReset={onReset}>
      {children}
    </MassChangeLifeCycle.Provider>
  );
}

function FilterSlot() {
  const view = useSearchParamsSelector(
    searchParamSelectors[searchParamKeys.view]
  );

  return view === viewMap.calendar ? <div>Filter</div> : null;
}
